import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SectionHeading } from "@/components/SectionHeading";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { formatDateID } from "@/lib/format";

export default function NewsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("Semua");

  useEffect(() => {
    api.get("/news").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const cats = useMemo(() => ["Semua", ...new Set(items.map((n) => n.category))], [items]);
  const filtered = items.filter(
    (n) =>
      (cat === "Semua" || n.category === cat) &&
      (n.title + " " + n.excerpt).toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#090A0F]">
      <Navbar />
      <main className="mx-auto max-w-7xl space-y-10 px-4 pb-24 pt-32 sm:px-6 lg:px-12">
        <SectionHeading label="Kabar Komunitas" title="Berita & Artikel"
          desc="Laporan touring, tips perawatan Captiva, dan kabar terbaru dari 3C Chapter Jatim." />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <button key={c} data-testid={`news-filter-${c.toLowerCase()}`} onClick={() => setCat(c)}
                className={`rounded-md border px-3.5 py-1.5 font-mono text-xs uppercase tracking-widest transition-colors ${
                  cat === c ? "border-rose-500/50 bg-rose-500/15 text-rose-400" : "border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200"
                }`}>
                {c}
              </button>
            ))}
          </div>
          <div className="relative sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input data-testid="news-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari berita..." className="border-white/10 bg-[#12141C] pl-10 text-slate-200 placeholder:text-slate-500" />
          </div>
        </div>
        {loading && <p className="text-slate-500">Memuat berita...</p>}
        {!loading && filtered.length === 0 && (
          <p data-testid="news-empty-state" className="rounded-xl border border-white/10 bg-[#12141C]/90 p-10 text-center text-slate-400">
            Tidak ada berita yang cocok.
          </p>
        )}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((n, i) => (
            <motion.article key={n.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              data-testid={`news-card-${n.id}`}
              className="group overflow-hidden rounded-xl border border-white/10 bg-[#12141C]/90 transition-all duration-300 hover:-translate-y-1 hover:border-rose-500/30">
              <Link to={`/berita/${n.id}`}>
                <div className="h-48 overflow-hidden">
                  <img src={n.image_url} alt={n.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="space-y-3 p-6">
                  <div className="flex items-center justify-between">
                    <span className="rounded-md border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 font-mono text-xs uppercase tracking-widest text-rose-400">{n.category}</span>
                    <span className="font-mono text-xs text-slate-500">{formatDateID(n.created_at)}</span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold leading-tight text-slate-100 group-hover:text-rose-400">{n.title}</h3>
                  <p className="line-clamp-3 text-sm leading-relaxed text-slate-400">{n.excerpt}</p>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
